import React, { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  getVisitorProfile,
  getChatMessages,
  getErrorMessage,
  type VisitorProfile,
  type VisitorProfileChat,
  type ChatTranscriptMessage,
} from '../../utils/api'
import { Badge } from '../shared/Badge'
import { LoadingSpinner } from '../shared/LoadingSpinner'
import { VisitorAvatar } from './VisitorDetailCard'
import { useSocket } from '../../contexts/SocketContext'
import type { MapVisitor } from './visitorMap.helpers'
import { formatLocalDateTimeWithTz } from './visitorMap.helpers'

/** Total time with us, formatted as e.g. "2h 14m" / "14m" / "0m". */
function formatTotalTime(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds || 0))
  if (s < 60) return `${s}s`
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  if (h > 0) return m ? `${h}h ${m}m` : `${h}h`
  return `${m}m`
}

function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

const CloseIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
)

const ChatIcon: React.FC = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
  </svg>
)

/* ─── Transcript modal ──────────────────────────────────────────────────────
 * Loads a single chat's messages via GET /chats/:id/messages and renders them
 * as a simple transcript (visitor right / agent+bot left). Same message-mapping
 * conventions the live chat thread uses (sender_type, message_type='system').
 */
const ChatTranscriptModal: React.FC<{
  chat: VisitorProfileChat
  onClose: () => void
}> = ({ chat, onClose }) => {
  const [messages, setMessages] = useState<ChatTranscriptMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    getChatMessages(chat.id)
      .then((res) => {
        if (cancelled) return
        setMessages(Array.isArray(res.messages) ? res.messages : [])
      })
      .catch((err) => {
        if (!cancelled) setError(getErrorMessage(err, 'Failed to load transcript'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [chat.id])

  // LIVE transcript (Lee, 17 Sep 2026): this modal used to load ONCE, so any
  // message sent while it was open — notably replies from the MOBILE app —
  // never appeared until reopen. Now it joins the chat's socket room and
  // appends message_received live, with a 5s poll as the belt-and-braces
  // (join_chat has access checks; polling covers a denied join too).
  const { socket } = useSocket()
  useEffect(() => {
    if (!socket) return
    socket.emit('join_chat', { chatId: chat.id })
    const onMsg = (m: any) => {
      if (String(m?.chat_id) !== String(chat.id)) return
      setMessages((prev) => (prev.some((x: any) => String(x.id) === String(m.id)) ? prev : [...prev, m]))
    }
    socket.on('message_received', onMsg)
    return () => {
      socket.off('message_received', onMsg)
      socket.emit('leave_chat', { chatId: chat.id })
    }
  }, [socket, chat.id])
  useEffect(() => {
    const t = setInterval(() => {
      getChatMessages(chat.id)
        .then((res) => setMessages(Array.isArray(res.messages) ? res.messages : []))
        .catch(() => { /* keep what we have */ })
    }, 5000)
    return () => clearInterval(t)
  }, [chat.id])

  // Escape closes (this modal is layered above the drawer).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onClose])

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Conversation transcript"
    >
      <div
        className="bg-surface rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-brand-100 dark:bg-brand-900/40 text-brand-600 dark:text-brand-300 flex items-center justify-center shrink-0">
              <ChatIcon />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-txt-primary truncate">
                {chat.company_name || chat.channel || 'Conversation'}
              </h3>
              <p className="text-xs text-txt-secondary truncate">
                {shortDate(chat.created_at)}
                {chat.message_count ? ` · ${chat.message_count} message${chat.message_count === 1 ? '' : 's'}` : ''}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close transcript"
            className="text-txt-secondary hover:text-txt-primary p-1 rounded-md hover:bg-surface-hover shrink-0"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-surface-secondary/30">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="medium" />
            </div>
          ) : error ? (
            <p className="text-sm text-error text-center py-8">{error}</p>
          ) : messages.length === 0 ? (
            <p className="text-sm text-txt-secondary text-center py-8">No messages in this conversation</p>
          ) : (
            messages.map((m) => {
              const isSystem = m.message_type === 'system' || m.sender_type === 'system'
              if (isSystem) {
                return (
                  <div key={m.id} className="flex justify-center">
                    <span className="text-[11px] text-txt-secondary italic bg-surface border border-border/40 px-3 py-1 rounded-full">
                      {(m.body || '').replace(/<[^>]*>/g, '')}
                    </span>
                  </div>
                )
              }
              const isVisitor = m.sender_type === 'customer' || (m.sender_type !== 'agent' && m.sender_type !== 'bot')
              const senderName =
                m.sender_name || (m.sender_type === 'agent' ? 'Agent' : m.sender_type === 'bot' ? 'Bot' : 'Visitor')
              return (
                <div key={m.id} className={`flex ${isVisitor ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] ${isVisitor ? 'items-end' : 'items-start'}`}>
                    <p className={`text-[10px] font-semibold mb-0.5 ${isVisitor ? 'text-right text-brand-600' : 'text-emerald-600'}`}>
                      {senderName}
                    </p>
                    <div
                      className={`inline-block rounded-2xl px-3.5 py-2 text-[13px] leading-relaxed whitespace-pre-wrap break-words ${
                        isVisitor
                          ? 'bg-brand-600 text-white rounded-tr-md'
                          : 'bg-surface text-txt-primary border border-border/60 shadow-sm rounded-tl-md'
                      }`}
                    >
                      {(m.body || '').replace(/<[^>]*>/g, '')}
                    </div>
                    {m.created_at && (
                      <p className="text-[10px] text-txt-secondary/60 mt-0.5">
                        {formatLocalDateTimeWithTz(m.created_at)}
                      </p>
                    )}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}

/* ─── Stat tile ─────────────────────────────────────────────────────────────*/
const Stat: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="bg-surface-secondary rounded-lg p-3">
    <p className="text-[10px] font-semibold uppercase tracking-wider text-txt-tertiary">{label}</p>
    <p className="text-sm font-bold text-txt-primary mt-1 break-words">{value}</p>
  </div>
)

export interface VisitorDetailModalProps {
  visitor: MapVisitor
  onClose: () => void
}

/**
 * "Visitor details" drawer — opened from the VisitorDetailCard's "View details"
 * button (which is shown for both the map and the list selection). Loads the
 * aggregated profile (total time, conversations, companies) and lets each chat
 * be opened as a transcript.
 */
export const VisitorDetailModal: React.FC<VisitorDetailModalProps> = ({ visitor, onClose }) => {
  const [profile, setProfile] = useState<VisitorProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [openChat, setOpenChat] = useState<VisitorProfileChat | null>(null)

  const load = useCallback(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    getVisitorProfile(visitor.id)
      .then((p) => {
        if (!cancelled) setProfile(p)
      })
      .catch((err) => {
        if (!cancelled) setError(getErrorMessage(err, 'Failed to load visitor details'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [visitor.id])

  useEffect(() => load(), [load])

  // Escape closes the drawer (only when no transcript is layered above it).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !openChat) onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, openChat])

  const isOnline = profile ? profile.is_online : visitor.status === 'online' || visitor.is_online
  const displayName = (profile?.name || visitor.name || '').trim() || 'Anonymous visitor'

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-stretch justify-end bg-black/50"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Visitor details"
    >
      <div
        className="bg-surface w-full max-w-md h-full flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border">
          <div className="flex items-start gap-3 min-w-0">
            <VisitorAvatar visitor={visitor} size={44} />
            <div className="min-w-0">
              <h2 className="text-base font-bold text-txt-primary truncate">{displayName}</h2>
              {(profile?.companies?.[0]?.company_name || visitor.company_name) && (
                <p className="text-xs text-txt-secondary truncate">
                  {profile?.companies?.[0]?.company_name || visitor.company_name}
                </p>
              )}
              <div className="mt-1.5">
                <Badge variant="status" value={isOnline ? 'online' : 'offline'} />
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close visitor details"
            className="p-1 rounded-md text-txt-tertiary hover:text-txt-primary hover:bg-surface-hover transition-colors shrink-0"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 flex flex-col">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <LoadingSpinner size="medium" />
            </div>
          ) : error ? (
            <div className="text-center py-12 px-5">
              <p className="text-sm text-error">{error}</p>
              <button
                onClick={() => load()}
                className="mt-3 px-4 py-1.5 rounded-lg text-sm font-medium bg-brand-600 text-white hover:bg-brand-700 transition-colors"
              >
                Try again
              </button>
            </div>
          ) : profile ? (
            <>
              {/* Fixed top — stays put + evenly spaced while the list scrolls */}
              <div className="px-5 py-5 border-b border-border shrink-0 space-y-5">
                {/* Stats */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-5">
                  <Stat label="Total time with us" value={formatTotalTime(profile.total_time_seconds)} />
                  <Stat label="Conversations" value={profile.conversation_count ?? profile.chats.length} />
                  <Stat label="Visits" value={profile.visit_count} />
                  <Stat label="First seen" value={shortDate(profile.first_seen)} />
                  <Stat label="Last seen" value={shortDate(profile.last_seen)} />
                </div>
                {profile.email && (
                  <p className="text-xs text-txt-secondary break-words">
                    <span className="font-medium text-txt-tertiary">Email: </span>
                    {profile.email}
                  </p>
                )}
                {/* Companies */}
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-txt-tertiary mb-2">
                    Appeared under
                  </h3>
                  {profile.companies.length === 0 ? (
                    <p className="text-sm text-txt-secondary">No company association.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {profile.companies.map((c) => (
                        <span
                          key={c.alias}
                          className="inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-surface-secondary border border-border"
                        >
                          {c.company_logo_url ? (
                            <img
                              src={c.company_logo_url}
                              alt=""
                              className="w-5 h-5 rounded-full object-cover bg-surface-tertiary"
                            />
                          ) : (
                            <span className="w-5 h-5 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 text-[9px] font-bold flex items-center justify-center">
                              {(c.company_name || c.alias || '?').slice(0, 2).toUpperCase()}
                            </span>
                          )}
                          <span className="text-xs font-medium text-txt-primary">
                            {c.company_name || c.alias}
                          </span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Conversations — header stays static, only this list scrolls */}
              <div className="flex-1 min-h-0 flex flex-col">
                <div className="px-5 pt-4 pb-2 shrink-0">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-txt-tertiary">
                    Conversations ({profile.chats.length})
                  </h3>
                </div>
                <div className="flex-1 overflow-y-auto px-5 pb-5">
                  {profile.chats.length === 0 ? (
                    <div className="rounded-lg bg-surface-secondary border border-border px-4 py-5 text-center">
                      <p className="text-sm font-medium text-txt-secondary">No conversations yet</p>
                      <p className="text-xs text-txt-tertiary mt-1">
                        {profile.email
                          ? "This visitor hasn't started any chats with us."
                          : "This visitor isn't identified (no email), so we can't link any chat history to them."}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {profile.chats.map((chat) => (
                        <button
                          key={chat.id}
                          onClick={() => setOpenChat(chat)}
                          className="w-full text-left rounded-lg border border-border bg-surface hover:bg-surface-hover transition-colors px-3 py-2.5"
                        >
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-xs font-medium text-txt-primary truncate">
                                {chat.company_name || chat.channel}
                              </span>
                              {chat.status && <Badge variant="status" value={chat.status} />}
                            </div>
                            <span className="text-[11px] text-txt-tertiary shrink-0 tabular-nums">
                              {shortDate(chat.created_at)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-[11px] text-txt-secondary truncate min-w-0">
                              {chat.preview || 'No messages'}
                            </p>
                            <span className="text-[10px] text-txt-tertiary shrink-0">
                              {chat.message_count} msg{chat.message_count === 1 ? '' : 's'}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>

      {openChat && <ChatTranscriptModal chat={openChat} onClose={() => setOpenChat(null)} />}
    </div>,
    document.body
  )
}
